/**
 * useEditHighlight — briefly marks the rows / chips a VQE edit touched, for as
 * long as the surface's "Edit applied · Undo" status shows, so the status and
 * the change it describes read as one thing.
 *
 * Generic on purpose: it never asks a renderer which chip an edit produced.
 * Clause items already carry index-suffixed test ids (`…-item-3`, `…-pill-0`,
 * `…-join-1`, `…-assignment-2`, …). The hook snapshots those items when the
 * user acts (pointer-down / Enter, capture phase — before any edit can land),
 * and after an edit compares the items then on screen with that snapshot. An
 * item counts as touched when its signature — the id with every index
 * replaced, plus the item's OWN text (text inside nested items belongs to
 * them) — is new: a new column chip, a filter whose value changed, a join row,
 * columns re-qualified by a join. Unchanged items shifted to another index keep
 * their signature, so inserting at the front does not light up the whole list.
 *
 * Marking is one attribute (`data-vqe-edit-highlight`, styled in index.css —
 * a static tint; the short fade-in is off under prefers-reduced-motion). A
 * MutationObserver re-applies it for a moment after the edit, because the
 * compact popover re-renders from a debounced buffer sync and React may
 * replace an item's node. When the status hides (timer, Undo, typing, Execute)
 * every mark is removed.
 */
import { useEffect, useRef } from 'react';

export const EDIT_HIGHLIGHT_ATTR = 'data-vqe-edit-highlight';

/** Clause items: a test id that ends in `-<kind>-<index>`. */
const ITEM_TESTID = /-(?:item|pill|join|assignment|branch|row|column|cell)-\d+$/;
/** How long after an edit landed its re-renders still count as that edit. */
const SETTLE_MS = 1500;

type ItemSignature = { readonly el: HTMLElement; readonly sig: string };

function isItem(el: Element): el is HTMLElement {
  const id = el.getAttribute('data-testid');
  return !!id && ITEM_TESTID.test(id);
}

/** Regions that hold no clause items (the results grid re-renders on its own). */
const IGNORED_REGION = '[data-testid="vqe-results-section"]';

/** Text of `item` that does not sit inside a nested item. */
function ownText(item: HTMLElement, items: ReadonlySet<Element>): string {
  let text = '';
  const walker = item.ownerDocument.createTreeWalker(item, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    let owner: Element | null = node.parentElement;
    while (owner && owner !== item && !items.has(owner)) owner = owner.parentElement;
    if (owner === item) text += node.textContent ?? '';
  }
  return text;
}

/** Every clause item under `root`, with its signature, in DOM order. */
export function collectItemSignatures(root: HTMLElement): ItemSignature[] {
  const items = Array.from(root.querySelectorAll('[data-testid]')).filter(
    (el): el is HTMLElement => isItem(el) && !el.closest(IGNORED_REGION),
  );
  const itemSet = new Set<Element>(items);
  return items.map((el) => {
    const kind = (el.getAttribute('data-testid') ?? '').replace(/-\d+(?=-|$)/g, '-#');
    const text = ownText(el, itemSet).replace(/\s+/g, ' ').trim();
    return { el, sig: `${kind}|${text}` };
  });
}

function countSignatures(items: readonly ItemSignature[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const { sig } of items) counts.set(sig, (counts.get(sig) ?? 0) + 1);
  return counts;
}

/** Items whose signature was not in `before` (multiset — duplicates count). */
export function touchedItems(before: ReadonlyMap<string, number>, after: readonly ItemSignature[]): HTMLElement[] {
  const remaining = new Map(before);
  const touched: HTMLElement[] = [];
  for (const { el, sig } of after) {
    const left = remaining.get(sig) ?? 0;
    if (left > 0) remaining.set(sig, left - 1);
    else touched.push(el);
  }
  return touched;
}

export type UseEditHighlightArgs = {
  /** The surface's root element (full-tab workspace, compact popover content). */
  readonly getRoot: () => HTMLElement | null;
  /** The "Edit applied" status is showing. */
  readonly active: boolean;
  /** Changes once per shown edit (a new edit while visible re-marks). */
  readonly editToken: number | null;
};

export function useEditHighlight({ getRoot, active, editToken }: UseEditHighlightArgs): void {
  const getRootRef = useRef(getRoot);
  getRootRef.current = getRoot;
  const baselineRef = useRef<Map<string, number> | null>(null);
  const markedRef = useRef<Set<HTMLElement>>(new Set());

  // Snapshot before the user's action can change anything (capture phase).
  useEffect(() => {
    const snapshot = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Enter') return;
      const root = getRootRef.current();
      if (root?.isConnected) baselineRef.current = countSignatures(collectItemSignatures(root));
    };
    document.addEventListener('pointerdown', snapshot, true);
    document.addEventListener('keydown', snapshot, true);
    return () => {
      document.removeEventListener('pointerdown', snapshot, true);
      document.removeEventListener('keydown', snapshot, true);
    };
  }, []);

  useEffect(() => {
    const clear = () => {
      for (const el of markedRef.current) el.removeAttribute(EDIT_HIGHLIGHT_ATTR);
      markedRef.current.clear();
    };
    const root = getRootRef.current();
    const baseline = baselineRef.current;
    // Nothing to compare against (no user action seen, or a surface that had
    // no items — e.g. the starter canvas turning into a SELECT view): mark nothing.
    if (!active || editToken === null || !root || !baseline || baseline.size === 0) {
      clear();
      return undefined;
    }
    const apply = () => {
      const touched = touchedItems(baseline, collectItemSignatures(root));
      clear();
      for (const el of touched) {
        el.setAttribute(EDIT_HIGHLIGHT_ATTR, '');
        markedRef.current.add(el);
      }
    };
    apply();
    // A MutationObserver callback runs after the whole (synchronous) React
    // commit, so each call sees a finished render. Only child / text changes
    // are observed — our own attribute writes never re-trigger it.
    const observer = new MutationObserver(apply);
    observer.observe(root, { subtree: true, childList: true, characterData: true });
    const settle = setTimeout(() => observer.disconnect(), SETTLE_MS);
    return () => {
      clearTimeout(settle);
      observer.disconnect();
      clear();
    };
  }, [active, editToken]);
}
